<script>
    import date from '../lib/date';

    import API from '../api';

    // Get video js Czech translation
    import csLang from '../../public/videojs/lang/cs.json';

    //import Rate from './stream/rate.svelte';

    export let recId;
    export let clnId;
    export let code;

    // State management
    let loading = true;
    let error = false;
    let expiredOn = null;
    let recording = null;
    let token = null;
    let tokenExpired = false;
    let videoUrl = null;
    let videoType = null;
    let showPlayer = false;

    // Elements / controllers
    let videoPlaceholder = null;
    let player = null;

    // Stream check interval
    let refreshInterval = null;

    async function onReady() {
        // Starting loading
        loading = true;

        // Get startup time
        let startTime = new Date();

        // Get stream info
        let sub = await API.recordingSubscribe(recId, clnId, code);

        // Calculate elapsed time
        let elapsed = (new Date()) - startTime;

        // Minimum wait
        let delay = 500 - elapsed;
        if (delay > 0) {
            await new Promise((resolve) => setTimeout(resolve, delay));
        }

        // Failed to get stream info
        if (!sub) {
            loading = false;
            error = true;
            return;
        }

        // Expired
        if (sub.expired) {
            loading = false;
            expiredOn = sub.expired;
            return;
        }

        // Get subscriber token
        token = sub.token;

        // Get viewer info
        //viewer = sub.viewer;

        // Get event info
        recording = sub.recording;

        // Get stream status
        let stream = await API.recordingCheck(token);

        // Oh no, race condition
        if (!stream) {
            tokenExpired = true;
            loading = false;
            return;
        }

        // Save properties
        videoUrl = stream.url;
        videoType = stream.type;

        // Done the first load
        loading = false;

        // Refresh stream info every 5s
        refreshInterval = setInterval(streamCheck, 5 * 1000);
    }

    async function streamCheck() {
        // Get stream status
        let stream = await API.recordingCheck(token);

        // Oh no, your token expired, how unfortunate!
        if (!stream) {
            tokenExpired = true;
            clearInterval(refreshInterval);
            return;
        }
    }

    /* async function onRate({ detail: e }) {
        if (e.value === null) {
            e.value = -1;
        }

        isRating = true;
        await API.streamRate(token, e.type, e.value);
        isRating = false;
    } */

    $: placeholderReady(videoPlaceholder);
    async function placeholderReady(videoPlaceholder) {
        if (!videoPlaceholder) return;

        // Create our own video element
        const video = document.createElement('video');
        video.classList.add('video-js');
        video.addEventListener('contextmenu', e => e.preventDefault());
        videoPlaceholder.parentElement.insertBefore(video, videoPlaceholder);

        // Setup video player
        await new Promise((resolve) => {
            player = videojs(video, {
                language: 'cs',
                //aspectRatio: '16:9',
                fill: true,
                liveui: false,
                controls: true,
                muted: (location.host.indexOf('test') >= 0),
                controlBar: {
                    pictureInPictureToggle: false
                }
            }, resolve);
        });

        // Handle errors
        player.on('error', (...args) => {
            error = true;
        });

        // Interval for destroying player on pause
        let pauseKillInterval = null;

        // Clear pause interval
        player.on('play', () => {
            pauseKillInterval && clearInterval(pauseKillInterval);
        });

        // Handle pause, go back to pre-play state
        player.on('pause', () => {
            pauseKillInterval = setTimeout(() => {
                showPlayer = false;
            }, 300);
        });

        // Set source playlist
        player.src({
            src: videoUrl,
            type: videoType
        });

        // Try to start playing
        player.play();
    }

    // Do cleanup after removing video element
    function onVideoMount() {
        return {
            destroy: () => {
                // Video.js cleanup
                player.dispose();

                // Remove references
                player = null;
            }
        };
    }

    function onScriptLoad() {
        if (!window.videojs) return;

        // Add Czech translation for Video.js
        videojs.addLanguage('cs', csLang);

        // Start loading
        onReady();
    }
</script>

<svelte:head>
    <link href="/videojs/video-js.min.css" rel="stylesheet">
    <!-- <script src="/videojs/video.min.js" on:load={onScriptLoad}></script> -->
    <script src="/videojs/video.js" on:load={onScriptLoad}></script>
</svelte:head>

<style>
    .column-container {
        display: flex;
        height: 100%;
        flex: 1;
        background: #181818;
    }

    .video-container {
        display: flex;
        flex: 1;
        background: #0c0c0c;
    }

    .column-container._loading {
        display: flex;
        justify-content: center;
        align-items: center;
    }

    @keyframes pulse {
        0%, 100% {
            width: 80px;
            height: 80px;
            background: #5656ff;
        }

        50% {
            width: 120px;
            height: 120px;
            background: #3a3aff;
        }
    }

    .column-container._loading::before {
        content: '';
        display: block;
        width: 80px;
        height: 80px;
        border-radius: 50%;
        background: #5656ff;
        animation: pulse 800ms infinite ease-in-out;
    }

    .message {
        align-self: flex-start;
        width: 400px;
        max-width: 100%;
        margin: 60px auto 0 auto;
        padding: 16px;
        border-radius: 8px;
        box-shadow: inset 0 0 0 2px #0c569e;
        background: rgba(0,0,0,0.4);
        color: white;
    }

    .column-container._error {
        justify-content: center;
    }

    .column-container._error .message {
        box-shadow: inset 0 0 0 2px #6f1515;
    }
</style>

<div class="column-container" class:_loading={loading} class:_error={tokenExpired || expiredOn || error}>
    {#if error}
        <div class="message">
            Při načítání došlo k chybě!<br>
            Zkuste obnovit stránku.
        </div>
    {:else if expiredOn}
        <div class="message">
            Platnost objednávky vypršela!<br>
            Objednávka byla platná do {date.format(expiredOn, `${date.FORMAT_DATE_HUMAN} ${date.FORMAT_TIME_HUMAN}`)}
        </div>
    {:else if tokenExpired}
        <div class="message">
            Nahrávka je přehrávána na jiném zařízení/okně.<br>
            Pro pokračování ve sledování na tomto zařízení obnovte stránku
        </div>
    {:else if videoUrl}
        <div class="video-container">
            <div class="video-placeholder" bind:this={videoPlaceholder} use:onVideoMount></div>
        </div>
    {/if}
</div>